import { supabase } from "../lib/supabase";
import { extractFunctionErrorMessage } from "./edgeFunctionErrors";
import { createOauthFunctionClient } from "./oauthFunctionClient";

/**
 * Normalizes a store domain before sending it:
 * trims whitespace, lowercases, strips a leading http(s):// scheme and any
 * trailing slashes, and appends .myshopify.com to a bare store name (no dot
 * at all) so "acme" and "acme.myshopify.com" both resolve the same way.
 */
export function normalizeShopDomain(raw) {
  let value = (raw ?? "").trim().toLowerCase();
  if (!value) return "";
  value = value.replace(/^https?:\/\//, "").replace(/\/+$/, "");
  if (value && !value.includes(".")) {
    value = `${value}.myshopify.com`;
  }
  return value;
}

// Every call carries the user's JWT and attaches the HTTP status to a thrown
// error (err.status), so SalesIntegrationsSection's install-origin confirm
// prompt can tell a definitive rejection from a transient failure and decide
// whether to clear the persisted confirm token/nonce or keep them for a retry.
const { invoke, requestUrl } = createOauthFunctionClient("shopify-oauth");

/**
 * Starts the Shopify OAuth connect flow: asks shopify-oauth for an authorize
 * URL scoped to this advertiser and shop, then navigates the browser there.
 * Called from salesSources.js's `startConnect(advertiserId, authToken, shop)`
 * with the reconnecting connection's own stored shop domain (never a typed
 * one, Shopify App Store requirement 2.3.1); this normalizes defensively and
 * the Edge Function only accepts a valid *.myshopify.com domain.
 */
export async function startShopifyConnect(advertiserId, authToken, shop) {
  const normalized = normalizeShopDomain(shop);
  const url = await requestUrl("start", advertiserId, authToken, {
    shop: normalized,
    return_url: window.location.origin,
  }, "Shopify did not return an authorize URL");
  window.location.assign(url);
}

/**
 * A random, URL-safe (base64url) nonce the SPA generates client-side and
 * keeps in localStorage BEFORE calling beginShopifyInstall (DEV-2332
 * code-review round). This is what binds the eventual confirm step to the
 * browser that actually started the install: the server hashes this value
 * into the signed install state and later requires the SAME raw value back
 * at confirm time (see confirmShopifyConnect below) before it will bind a
 * null-owner pending_connections row to any advertiser. Without it, the
 * confirm token alone is a pure bearer credential — it can leak via
 * PostHog/history/server logs, and a stripped `origin=install` link would
 * otherwise fall into the ordinary automatic (bound-flow) confirm path.
 * 32 random bytes, same recipe as shopify-oauth/shopify.ts's
 * generateConfirmToken (kept separate rather than shared: one is
 * Deno/TypeScript, the other the CRA bundle).
 */
// Implementation lives in lib/shopifyInstallRedirect.js so the Edge middleware
// shares it; re-exported here for existing callers.
export { generateInstallNonce } from "../lib/shopifyInstallRedirect";

/**
 * Starts an install Shopify itself initiated (DEV-2332: OAuth-first),
 * with NO Audiohook session at all — this is what lets Shopify's OAuth
 * grant screen open before the Audiohook login. Unlike every other action
 * here, this is called with no Authorization header: shopify-oauth's
 * `begin-install` action runs before any JWT check. The shop is not
 * merchant-entered; it comes from Shopify's signed install query, captured
 * by AuthGate's capture effect and forwarded verbatim. The server verifies
 * the HMAC, timestamp freshness and shop domain itself; the client trusts
 * nothing about the query beyond forwarding it.
 *
 * `installNonce` (DEV-2332 code-review round) is generateInstallNonce()'s
 * raw output — sent so the server can hash it into the signed state.
 * ALWAYS returns `{ url }` now — the earlier `already_connected` shortcut
 * was removed (Shopify requires OAuth even on a shop that was previously
 * installed and uninstalled).
 */
export async function beginShopifyInstall(installQuery, installNonce) {
  if (!supabase) {
    throw new Error("Supabase client is not initialized");
  }
  const { data, error } = await supabase.functions.invoke("shopify-oauth", {
    body: { action: "begin-install", install_query: installQuery, install_nonce: installNonce },
  });
  if (error) {
    throw new Error(await extractFunctionErrorMessage(error));
  }
  return data;
}

/**
 * Calls `beginShopifyInstall` and maps its outcome (including a thrown
 * error) to a single discriminated result AuthGate's capture effect can act
 * on without knowing anything about the Edge Function's response shape:
 *
 *   - `{ kind: "redirect", url }` — navigate the browser to Shopify's
 *     authorize screen.
 *   - `{ kind: "invalid" }` — a tampered/stale link or a server error;
 *     callers surface the existing `install_link_invalid` note.
 *
 * Never throws — every failure mode (a thrown error, a malformed response
 * with no `url`) collapses to `{ kind: "invalid" }`, so callers can treat
 * this as a pure outcome mapper. No `already_connected` kind (DEV-2332
 * code-review round: begin-install always returns an authorize URL now).
 */
export async function startShopifyInstallFromQuery(installQuery, installNonce) {
  try {
    const result = await beginShopifyInstall(installQuery, installNonce);
    if (result?.url) {
      return { kind: "redirect", url: result.url };
    }
    return { kind: "invalid" };
  } catch {
    return { kind: "invalid" };
  }
}

/**
 * Confirms a staged Shopify connection (code-review MEDIUM #2's
 * confirm-in-app fix): called from SalesIntegrationsSection once with the
 * `confirm` token off the OAuth callback's ?status=pending redirect. The
 * Edge Function checks the token against the CURRENT signed-in user and
 * advertiser before writing anything; a rejection surfaces as a thrown Error
 * with the Edge Function's generic ("invalid or has expired") message —
 * never which specific check failed.
 *
 * `installNonce` (DEV-2332 code-review round), when present, is the raw
 * value read back from the SPA's own localStorage for an install-origin
 * confirm — omitted (null) for the ordinary bound (in-app) flow, which
 * never has one.
 */
export async function confirmShopifyConnect(advertiserId, confirmToken, authToken, installNonce = null) {
  const extra = { confirm_token: confirmToken };
  if (installNonce) extra.install_nonce = installNonce;
  return invoke("confirm", advertiserId, authToken, extra);
}
